import type { ChargePaymentMethod } from '../../../api/schemas';
import { paymentMethodLabel } from '../../../lib/paymentMethods';
import type { LineId } from '../voice/lines';
import { biggest, count, labelOf, mercurio, money, onReport, type Reply } from './replyKit';

/** The way most money arrives, said out loud. */
const MAIN_METHOD: Readonly<Record<ChargePaymentMethod, LineId>> = {
  efectivo: 'metodo-efectivo',
  tarjeta: 'metodo-tarjeta',
  yappy: 'metodo-yappy',
  transferencia: 'metodo-transferencia',
  banco_aliado: 'metodo-banco',
  otro: 'metodo-otro',
};

const TOP_METHODS = 4;
const TOP_PEOPLE = 3;

export const porCobrar: Reply = onReport('mes', (report) => {
  const owed = report.sales.outstandingCents;
  if (owed <= 0) return mercurio('porcobrar-cero');
  return mercurio('porcobrar-hay', { figures: [money('Por cobrar', owed)] });
});

export const metodo: Reply = onReport('mes', (report) => {
  const ranked = biggest(report.paymentMethods, (entry) => entry.amountCents, TOP_METHODS);
  const top = ranked[0];
  if (!top) return mercurio('metodo-nada');
  return mercurio(MAIN_METHOD[top.method], {
    note: labelOf(report),
    figures: ranked.map((entry) => money(paymentMethodLabel(entry.method), entry.amountCents)),
  });
});

export const servicio: Reply = onReport('mes', (report) => {
  const ranked = biggest(report.services, (entry) => entry.revenueCents, TOP_PEOPLE);
  if (ranked.length === 0) return mercurio('servicio-nada');
  return mercurio('servicio-top', {
    note: labelOf(report),
    figures: ranked.map((entry) => money(entry.name, entry.revenueCents)),
  });
});

export const equipo: Reply = onReport('mes', (report) => {
  const ranked = biggest(report.team, (entry) => entry.revenueCents, TOP_PEOPLE);
  if (ranked.length === 0) return mercurio('equipo-nada');
  return mercurio('equipo-top', {
    note: labelOf(report),
    figures: ranked.map((entry) => money(entry.name, entry.revenueCents)),
  });
});

export const cliente: Reply = onReport('mes', (report) => {
  const ranked = biggest(report.customers.top, (entry) => entry.revenueCents, TOP_PEOPLE);
  if (ranked.length === 0) return mercurio('cliente-nada');
  return mercurio('cliente-top', {
    note: labelOf(report),
    figures: ranked.map((entry) => money(entry.name, entry.revenueCents)),
  });
});

export const ticket: Reply = onReport('mes', (report) => {
  const { averageTicketCents, chargeCount } = report.sales;
  if (averageTicketCents <= 0) return mercurio('ventas-nada');
  return mercurio('ticket-hay', {
    note: labelOf(report),
    figures: [money('Ticket promedio', averageTicketCents), count('Cobros', chargeCount)],
  });
});

export const faltas: Reply = onReport('mes', (report) => {
  const { noShow, cancelled, noShowValueCents, cancelledValueCents } = report.bookings;
  if (noShowValueCents + cancelledValueCents <= 0) return mercurio('faltas-cero');
  return mercurio('faltas-hay', {
    note: labelOf(report),
    figures: [money(`No llegaron (${noShow})`, noShowValueCents), money(`Cancelaron (${cancelled})`, cancelledValueCents)],
  });
});

export const impuesto: Reply = onReport('mes', (report) => {
  const { invoiceCount, itbmsCents } = report.taxes;
  if (invoiceCount <= 0) return mercurio('impuesto-nada');
  return mercurio('impuesto-hay', {
    note: labelOf(report),
    figures: [money('ITBMS', itbmsCents), count('Facturas emitidas', invoiceCount)],
  });
});
